import confetti from 'canvas-confetti';

// Helper function for random values
function randomInRange(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

/**
 * Plays a celebration animation when a task is completed
 */
export function playCelebrationAnimation() {
  // Check if animations are disabled in accessibility settings
  const savedSettings = localStorage.getItem("accessibilitySettings");
  const settings = savedSettings ? JSON.parse(savedSettings) : {};
  const reducedMotion = settings?.reducedMotion || false;
  
  if (!reducedMotion) {
    const duration = 2000;
    const animationEnd = Date.now() + duration;

    // Create a multicolor confetti effect
    const defaults = { 
      startVelocity: 30, 
      spread: 360, 
      ticks: 60, 
      zIndex: 100,
      colors: ['#FF5252', '#4CAF50', '#2196F3', '#FFEB3B', '#9C27B0', '#FF9800']
    };

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // since particles fall down, start a bit higher than random
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  }
  
  // Play success sound
  playSound('success');
}

/**
 * Audio feedback functions
 */
const audioElements: {[key: string]: HTMLAudioElement} = {};
const audioCategoryElements: {[category: string]: {[key: string]: HTMLAudioElement}} = {
  timerStart: {},
  timerEnd: {},
};

// Available sound categories and options
export type SoundCategory = 'timerStart' | 'timerEnd';
export type SoundOption = string; // The filename without extension

// Default sound options for each category
const defaultSoundOptions: Record<SoundCategory, SoundOption[]> = {
  timerStart: ['gentle-bell', 'soft-chime', 'digital-beep', 'friendly-tone', 'alert'],
  timerEnd: ['achievement', 'celebration', 'complete', 'gentle-finish', 'applause'],
};

// Preload basic audio files
export function preloadAudio() {
  const audioElements: { [key: string]: HTMLAudioElement } = {};
  
  const sounds = {
    success: './sounds/success.mp3',
    click: './sounds/click.mp3',
    timerEnd: './sounds/timerEnd/complete.mp3',
    taskComplete: './sounds/task-complete.mp3',
  };

  const timerStartSounds = [
    'gentle-bell',
    'soft-chime', 
    'digital-beep',
    'friendly-tone',
    'alert'
  ];

  const timerEndSounds = [
    'achievement',
    'celebration',
    'complete',
    'gentle-finish',
    'applause'
  ];

  // Create a loading promise for each sound
  const loadingPromises = Object.entries(sounds).map(([key, src]) => {
    return new Promise<void>((resolve) => {
      const audio = new Audio();
      
      audio.oncanplaythrough = () => {
        console.log(`Sound loaded: ${key}`);
        resolve();
      };
      
      audio.onerror = (e) => {
        console.warn(`Error loading sound: ${key}`, e);
        resolve(); // Resolve anyway to not block other sounds
      };
      
      audio.preload = 'auto';
      audio.src = src;
      audioElements[key] = audio;
    });
  });

  // Preload timer start sounds
  timerStartSounds.forEach(sound => {
    const audio = new Audio(`./sounds/timerStart/${sound}.mp3`);
    audio.preload = 'auto';
    audioElements[`timerStart_${sound}`] = audio;
  });

  // Preload timer end sounds
  timerEndSounds.forEach(sound => {
    const audio = new Audio(`./sounds/timerEnd/${sound}.mp3`);
    audio.preload = 'auto';
    audioElements[`timerEnd_${sound}`] = audio;
  });
  
  // Preload categorized sounds and wait for all to complete
  Promise.all([
    ...loadingPromises,
    preloadCategorySounds('timerStart'),
    preloadCategorySounds('timerEnd')
  ])
  .then(() => {
    console.log("All audio files preloaded successfully");
  })
  .catch(error => {
    console.error("Error preloading audio files:", error);
  });
}

// Preload all sounds for a specific category
export function preloadCategorySounds(category: SoundCategory): Promise<void> {
  return new Promise<void>((resolve) => {
    if (!audioCategoryElements[category]) {
      audioCategoryElements[category] = {};
    }
    
    const options = defaultSoundOptions[category];
    if (!options || options.length === 0) {
      console.log(`No sounds to preload for category: ${category}`);
      resolve();
      return;
    }
    
    // Create a loading promise for each sound in the category
    const optionPromises = options.map(option => {
      return new Promise<void>((resolveOption) => {
        const audio = new Audio();
        
        // Set up event handlers before setting src
        audio.oncanplaythrough = () => {
          console.log(`Category sound loaded: ${category}/${option}`);
          resolveOption();
        };
        
        audio.onerror = (e) => {
          console.error(`Error loading category sound: ${category}/${option}`, e);
          resolveOption(); // Resolve anyway to not block other sounds
        };
        
        // Set preload attribute and src
        audio.preload = 'auto';
        audio.src = `/sounds/${category}/${option}.mp3`;
        
        // Store the audio element
        audioCategoryElements[category][option] = audio;
      });
    });
    
    // Wait for all sound options to load
    Promise.all(optionPromises)
      .then(() => {
        console.log(`All sounds preloaded for category: ${category}`);
        resolve();
      })
      .catch(error => {
        console.error(`Error preloading sounds for category ${category}:`, error);
        resolve(); // Resolve anyway to prevent blocking
      });
  });
}

// Get available sound options for a category
export function getSoundOptions(category: SoundCategory): SoundOption[] {
  return defaultSoundOptions[category] || [];
}

// Play a sound from a specific category
export function playCategorySound(category: SoundCategory, option: SoundOption) {
  try {
    // Check if sound is enabled in accessibility settings
    const savedSettings = localStorage.getItem("accessibilitySettings");
    const settings = savedSettings ? JSON.parse(savedSettings) : {};
    const soundEnabled = settings?.soundEnabled !== undefined ? settings.soundEnabled : true;
    
    if (!soundEnabled) return;
    
    console.log(`Attempting to play sound: ${category}/${option}`);
    
    // Get volume from settings (default to 70%)
    const volume = settings?.soundVolume !== undefined ? settings.soundVolume / 100 : 0.7;
    
    // If the sound hasn't been loaded yet, create and load it now
    if (!audioCategoryElements[category]?.[option]) {
      console.log(`Creating new audio element for ${category}/${option}`);
      const audio = new Audio();
      
      // Force autoplay policy by setting up the handlers before setting src
      audio.oncanplaythrough = () => {
        console.log(`Sound ready to play: ${category}/${option}`);
        // Auto-play when loaded if we're on a "play" function
        playLoadedAudio(audio, volume);
      };
      
      audio.onerror = (e) => {
        console.error(`Error loading sound ${category}/${option}:`, e);
        
        // Fallback to a beep sound if the original sound fails to load
        const fallbackBeep = new Audio();
        fallbackBeep.volume = volume;
        
        // Create a simple beep with the Web Audio API as a last resort
        try {
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.type = 'sine';
          oscillator.frequency.value = category === 'timerStart' ? 440 : 880; // A4 or A5
          gainNode.gain.value = volume;
          
          oscillator.start();
          setTimeout(() => oscillator.stop(), 200);
          
          console.log("Playing fallback beep sound");
        } catch (oscError) {
          console.error("Failed to create fallback beep:", oscError);
        }
      };
      
      // Set src and preload attributes
      audio.preload = 'auto';
      audio.src = `/sounds/${category}/${option}.mp3`;
      
      if (!audioCategoryElements[category]) {
        audioCategoryElements[category] = {};
      }
      
      audioCategoryElements[category][option] = audio;
      
      // Start loading the sound
      audio.load();
    } else {
      // Sound is already loaded, try to play it directly
      const audio = audioCategoryElements[category][option];
      playLoadedAudio(audio, volume);
    }
  } catch (error) {
    console.error(`Error playing ${category} sound:`, error);
    
    // Last resort fallback - try to create a new Audio element and play it immediately
    try {
      const tempAudio = new Audio(`/sounds/${category}/${option}.mp3`);
      tempAudio.volume = 0.7;
      tempAudio.play().catch(e => console.error("Fallback audio play failed:", e));
    } catch (fallbackError) {
      console.error("Even fallback audio failed:", fallbackError);
    }
  }
}

// Helper function to play an already loaded audio element
function playLoadedAudio(audio: HTMLAudioElement, volume: number) {
  // Set volume and reset playback position
  audio.volume = volume;
  audio.currentTime = 0;
  
  // Try to play the sound
  audio.play().then(() => {
    console.log("Sound playing successfully");
  }).catch(e => {
    console.error("Failed to play sound:", e);
    
    // If audio playback is blocked by autoplay policy, try again after user interaction
    if (e.name === 'NotAllowedError') {
      console.log("Autoplay blocked - will try again on next user interaction");
      
      // Add one-time event listener for user interaction to try playing again
      const playOnInteraction = () => {
        audio.play().catch(innerErr => console.error("Still failed to play after interaction:", innerErr));
        
        // Remove the listeners after first interaction
        document.removeEventListener('click', playOnInteraction);
        document.removeEventListener('keydown', playOnInteraction);
      };
      
      document.addEventListener('click', playOnInteraction, { once: true });
      document.addEventListener('keydown', playOnInteraction, { once: true });
    }
  });
}

export function playSound(soundName: 'success' | 'click' | 'timerEnd' | 'taskComplete') {
  try {
    // Check if sound is enabled in accessibility settings
    const savedSettings = localStorage.getItem("accessibilitySettings");
    const settings = savedSettings ? JSON.parse(savedSettings) : {};
    const soundEnabled = settings?.soundEnabled !== undefined ? settings.soundEnabled : true;
    
    if (!soundEnabled) return;
    
    // Check for timer settings
    const timerSettings = localStorage.getItem("timer-settings");
    const timerConfig = timerSettings ? JSON.parse(timerSettings) : {};
    
    // Determine which sound file to use
    let soundFile = '';
    
    if (soundName === 'click') {
      const timerSound = timerConfig?.timerSound || 'gentle-bell';
      switch(timerSound) {
        case 'gentle-bell':
          soundFile = '/sounds/click.mp3';
          break;
        case 'soft-chime':
          soundFile = '/sounds/timerStart/soft-chime.mp3';
          break;
        case 'digital-beep':
          soundFile = '/sounds/timerStart/digital-beep.mp3';
          break;
        case 'alert':
          soundFile = '/sounds/timerStart/alert.mp3';
          break;
        case 'friendly-tone':
          soundFile = '/sounds/timerStart/friendly-tone.mp3';
          break;
        default:
          soundFile = '/sounds/click.mp3';
      }
    } else if (soundName === 'timerEnd') {
      const completionSound = timerConfig?.completionSound || 'gentle-finish';
      switch(completionSound) {
        case 'gentle-finish':
          soundFile = '/sounds/timerEnd/gentle-finish.mp3';
          break;
        case 'achievement':
          soundFile = '/sounds/timerEnd/achievement.mp3';
          break;
        case 'complete':
          soundFile = '/sounds/timerEnd/complete.mp3';
          break;
        case 'celebration':
          soundFile = '/sounds/timerEnd/celebration.mp3';
          break;
        case 'applause':
          soundFile = '/sounds/timerEnd/applause.mp3';
          break;
        default:
          soundFile = '/sounds/timerEnd/complete.mp3';
      }
    } else {
      // Use the default sounds for other types
      if (audioElements[soundName]) {
        const audio = audioElements[soundName];
        const volume = settings?.soundVolume !== undefined ? settings.soundVolume / 100 : 0.7;
        audio.volume = volume;
        audio.currentTime = 0;
        
        audio.play().catch(e => {
          console.error('Failed to play sound:', e);
          
          // If audio is not loaded yet, try loading it first
          if (e.name === 'NotAllowedError' || e.name === 'AbortError') {
            audio.load();
          }
        });
        
        return;
      } else {
        console.warn(`No audio element found for sound: ${soundName}`);
        return;
      }
    }
    
    // Get volume from settings (default to 70%)
    const volume = settings?.soundVolume !== undefined ? settings.soundVolume / 100 : 0.7;
    
    // Create a new audio element for the custom sounds
    const audio = new Audio(soundFile);
    audio.volume = volume;
    
    audio.play().catch(e => {
      console.error(`Failed to play sound ${soundFile}:`, e);
      
      // Fallback to the default sound if available
      if (audioElements[soundName]) {
        console.log(`Falling back to default sound for ${soundName}`);
        const fallbackAudio = audioElements[soundName];
        fallbackAudio.volume = volume;
        fallbackAudio.currentTime = 0;
        fallbackAudio.play().catch(fallbackErr => {
          console.error('Even fallback sound failed:', fallbackErr);
        });
      }
    });
  } catch (error) {
    console.error("Error playing sound:", error);
  }
}

/**
 * Visual feedback animations for UI elements
 */
export function animateElement(element: HTMLElement, animationType: 'pulse' | 'shake' | 'bounce') {
  if (!element) return;
  
  // Check if animations are disabled in accessibility settings
  const savedSettings = localStorage.getItem("accessibilitySettings");
  const settings = savedSettings ? JSON.parse(savedSettings) : {};
  const reducedMotion = settings?.reducedMotion || false;
  
  if (reducedMotion) return;
  
  // Remove any existing animation classes
  element.classList.remove('pulse', 'shake', 'bounce');
  
  // Force a reflow to restart animation if the same class is added again
  void element.offsetWidth;
  
  // Add the requested animation class
  element.classList.add(animationType);
  
  // Remove the animation class after it completes
  element.addEventListener('animationend', () => {
    element.classList.remove(animationType);
  }, { once: true });
}